/**
 * FAQ content — single source of truth.
 *
 * Consumed by both the /faq page UI (pages/Faq.jsx) and the FAQPage JSON-LD
 * structured data (seo.js → faqPageLd). Keeping it here means the schema
 * Google/AI crawlers read can never drift from what visitors actually see.
 *
 * Answers favor specific, citable facts (exact behaviors, named providers,
 * the model names) — those are what AI answer engines quote.
 */
export const CONTACT_EMAIL = "hello@handinvoice.com";

export const FAQ_QUESTIONS = [
  {
    q: "How does it work?",
    a: "You record yourself talking about a job, the app extracts the details (customer, hours, parts, etc.), and generates a PDF invoice. Speak naturally — like you're telling a coworker what you did.",
  },
  {
    q: "Do I need to create an account?",
    a: "No. You can record, review, and download a PDF without signing up. When you're not signed in, nothing about you is saved on our servers — any business details you enter stay only in your web browser on that device and disappear when you close the tab. Sign in only if you'd like your invoices saved to come back to later.",
  },
  {
    q: "What's the difference between anonymous and signed-in?",
    a: "Anonymous: nothing is saved on our end. You can record, review, and download a PDF, and any business details you enter live only in your browser. Signed in: the same thing, plus your invoices are saved to your account history (the Invoices page) and your business details are stored so they follow you across devices. We also keep a short activity log — which invoices you made, when, and the total — to understand usage; it never includes customer names or line items.",
  },
  {
    q: "Is my data secure?",
    a: "Yes. Voice recordings are never saved — they're held only briefly while you record and deleted the moment you finalize the invoice. Your connection is secure (the same lock-icon encryption banks use), and anything we do store is kept in scrambled, encrypted form. We never sell your data. If you use the app without signing in, even less reaches us in the first place. See the Privacy page for the full picture.",
  },
  {
    q: "What does it cost?",
    a: "Currently free during beta. We may introduce paid tiers later for advanced features (unlimited history, integrations, etc.).",
  },
  {
    q: "What if the AI gets something wrong?",
    a: "You can edit any field on the Review screen before generating the PDF. Nothing gets sent until you approve it.",
  },
  {
    q: "Can I use this on my phone?",
    a: 'Yes. The app works on any device with a browser. You can also "Add to Home Screen" on iOS or Android for an app-like experience.',
  },
  {
    q: "How accurate is the voice transcription?",
    a: "Very accurate for clear speech in normal noise. Speak at a normal pace, hold the phone within 6 inches of your mouth, and you should get clean results.",
  },
  {
    q: "Can I customize my invoices?",
    a: "Yes — quite a bit. In Settings you can add your business name, contact info, license number, a logo, default payment terms (e.g. 'due in 15 days'), invoice notes, payment methods, and tax defaults — these appear on every invoice. On the Review screen, before you generate the PDF, you can also override most of these for a single invoice (payment terms, customer-visible notes, business details, a discount) without changing your saved defaults.",
  },
  {
    q: "If I download an invoice again later, will it look exactly the same?",
    a: "The details are locked once you finalize an invoice — the amounts, customer, line items, and the business info on it never change. The PDF itself isn't stored as a file; it's regenerated from that saved data each time you download it, so the layout reflects the current version of HandInvoice. That means a later download may look slightly different from the one you first sent, even though the numbers are identical. Saving the exact original PDF file is on our roadmap.",
  },
  {
    q: "Is HandInvoice safe?",
    a: 'Yes. Your voice recording is never saved — it is held only while you record and permanently deleted when you finalize the invoice. We never sell or share your data and run no advertising trackers; we use a privacy-focused analytics tool (called PostHog) for basic, anonymous usage stats (see "Does HandInvoice track me?" below). Your connection is secure (lock-icon encryption) and anything stored is kept scrambled (encrypted). Signing in is handled by a dedicated login service (called Clerk), so HandInvoice never sees or stores your password.',
  },
  {
    q: "Does HandInvoice track me?",
    a: "Only anonymously. We use an analytics tool (called PostHog) to collect anonymous usage data — page views, button clicks, and which features people use — so we can improve the app. We do not record or replay your screen, we run no advertising trackers, and we never sell or share your data. It works the same whether or not you're signed in, and it only stores a random, anonymous ID in your browser that isn't tied to who you are.",
  },
  {
    q: "Is HandInvoice legit?",
    a: "Yes. HandInvoice is built by an independent developer. It is free to use during the current beta; a paid plan may be added later for advanced features. There is nothing to install, and no account is required to make an invoice. When you do sign in, your invoices are kept in a secure database and your login is handled by a dedicated sign-in service (Clerk) — so your password never touches our app.",
  },
  {
    q: "Who runs HandInvoice?",
    a: "HandInvoice is built and maintained by an independent developer who got tired of contractors complaining about clunky invoicing software. You can reach the team at " + CONTACT_EMAIL + ".",
  },
  {
    q: "Is this an AI thing? What AI does it use?",
    a: "Yes. HandInvoice uses AI from OpenAI: one model (called Whisper) turns your spoken words into text, and another (called GPT-4o) reads that text to pull out the invoice details — customer, hours, materials, tax. Your recordings and transcripts are not used to train AI models. You review and can edit every field before the PDF is created.",
  },
  {
    q: "What if I need help?",
    a: "Contact us at " + CONTACT_EMAIL + " — we read everything and reply quickly during beta.",
  },
];
